// Fixture del demo público de Materiales con IA (MaterialesClaseDemo.vue).
//
// El contenido NO viaja en el bundle: lo publica en el servidor
//
//     python manage.py capturar_demo_materiales --grade "…" --subject "…" --oa …
//
// y este módulo lo pide en tiempo de ejecución a /api/materiales-demo/fixture.
//
// Esa es la diferencia con el demo de Planificaciones, que importa su
// planificationDemoData.js y por lo tanto sólo cambia de contenido con un
// despliegue. Acá el demo se renueva cada semana con los OA que toca enseñar, y
// eso tiene que ser correr un comando, no publicar una versión.
//
// Acá no hay ninguna llamada a IA. Lo único que se hace es traer el JSON,
// resolver rutas relativas a URL absoluta y exponer el curso y la asignatura
// capturados, para que la pantalla NO los tenga escritos a mano: si la próxima
// captura es 5° básico · Ciencias, el demo lo dice solo.

const ENDPOINT = "/api/materiales-demo/fixture";

const abs = (path) =>
  path && !path.startsWith("http") ? window.location.origin + path : path;

/** El curso capturado, tal como lo devuelve el servicio de cursos. */
export function cursoCapturado(fixture) {
  const wizard = (fixture && fixture.wizard) || {};
  const grades = wizard.grades || [];
  return grades.find((g) => g.code === wizard.gradeCode) || grades[0] || null;
}

/** La asignatura capturada, tal como la devuelve el servicio de asignaturas. */
export function asignaturaCapturada(fixture) {
  const wizard = (fixture && fixture.wizard) || {};
  const subjects = wizard.subjects || [];
  return (
    subjects.find((s) => s.code === wizard.subjectCode) || subjects[0] || null
  );
}

/**
 * Los OAs que la captura usó de verdad, en el orden del plan.
 *
 * El wizard trae los 26 OAs del curso porque así los devuelve la API, pero sólo
 * unos pocos tienen pregunta capturada. Distinguirlos permite que la pantalla
 * pre-seleccione los que sí van a generar algo, en vez de dejar que el visitante
 * elija uno vacío y el demo se quede pensando.
 */
export function oasDelPlan(fixture) {
  const plan = (fixture && fixture.plan) || [];
  const vistos = new Set();
  const salida = [];
  plan.forEach((entry) => {
    if (!entry.oaId || vistos.has(entry.oaId)) return;
    vistos.add(entry.oaId);
    salida.push({ oaId: entry.oaId, oaName: entry.oaName });
  });
  return salida;
}

/**
 * Cuántos ejercicios DISTINTOS tiene capturados cada OA.
 *
 * El demo no puede mostrar más ejercicios que los que la captura trae: pedirle
 * dos actividades a un OA con una sola pregunta capturada devuelve la misma dos
 * veces, o sea el generador mostrando que no varía. Con este conteo el guión
 * sabe cuántos OAs marcar —y cuántas actividades pedirle a cada uno— para llegar
 * al total que quiere sin repetir ninguno.
 *
 * @returns {Object<string, number>} oaId → cuántas preguntas tiene.
 */
export function preguntasPorOa(fixture) {
  const plan = (fixture && fixture.plan) || [];
  const preguntas = (fixture && fixture.questions) || [];
  const conteo = {};
  preguntas.forEach((pregunta, i) => {
    const entrada = plan[i] || {};
    const oaId = entrada.oaId || pregunta.oaId;
    if (!oaId) return;
    conteo[oaId] = (conteo[oaId] || 0) + 1;
  });
  return conteo;
}

/** Se pide una sola vez por carga de página, aunque el visitante reinicie el chat. */
let enVuelo = null;

/**
 * Trae el contenido del demo, o `null` si este servidor todavía no lo tiene.
 *
 * Devolver `null` en vez de lanzar es deliberado: un servidor recién desplegado
 * —o uno donde nadie corrió la captura— no tiene demo, y eso no es un error que
 * haya que mostrarle al visitante. La pantalla simplemente no ofrece la opción.
 * El caso contrario, ofrecerla y que no cargue nada, es el que hay que evitar.
 */
export function cargarFixture() {
  if (enVuelo) return enVuelo;
  enVuelo = fetch(ENDPOINT, { headers: { Accept: "application/json" } })
    .then((res) => {
      if (!res.ok) {
        // 404 es el caso esperado y silencioso: no se capturó todavía.
        if (res.status !== 404) {
          console.warn(
            "[materialsDemoFixture] el servidor respondió %s al demo",
            res.status
          );
        }
        return null;
      }
      return res.json();
    })
    .then((data) => {
      if (!data || !Array.isArray(data.questions) || !data.questions.length) {
        // Un JSON válido pero sin preguntas daría un demo que abre y no muestra
        // nada, que es peor que no ofrecerlo.
        return null;
      }
      return {
        ...data,
        downloads: Object.entries(data.downloads || {}).reduce(
          (acc, [key, value]) => {
            acc[key] = abs(value);
            return acc;
          },
          {}
        ),
      };
    })
    .catch((error) => {
      console.warn("[materialsDemoFixture] no se pudo cargar el demo:", error);
      return null;
    });
  return enVuelo;
}

export default cargarFixture;
